import {
  generateReadOnly,
  generateNewReadOnly,
  sendQuotation
} from "Api/quotationApi";
import iconGrid3 from "icon/win10/grid-3.svg?component";
import iconGrid2 from "icon/win10/grid-2.svg?component";
import intersectionBy from "lodash/intersectionBy";
import {
  getRequest,
  getRequestList,
  getLastBudgetList,
  getSupplierEvaluationList,
  updateRequestStatus,
  getRequestStatus,
  getRequestReview,
  addCompanyReview,
  updateCompanyReview,
  getAuthorsList,
  getAddressList,
  getQuoteStatus,
  getQuoteMostRatedCategories,
  getLastestActivitiesList,
  getLastMessageList,
  getArchivedQuotations
} from "Api/quoteRequestApi";
import {
  parseRequestPreview,
  parseRequestDetails,
  parseRequestPreviewStatus,
  parseRequestReview,
  parseLastBudget,
  parseLastestActivities,
  parseLastMessages,
  redoQuoteParser
} from "Parsers/quoteRequestParser";
import {
  formatRequestReviewAdd,
  formatRequestReviewUpdate,
  formatGenerateReadOnly
} from "Formatters/quoteRequestFormatter";
import { cardViewTypes } from "Enums/interfacePreferenceTypes";
import formatDate from "date-fns__legacy__/format";

const state = {
  requesting: {
    requestList: false,
    details: false,
    status: false,
    reviewList: false,
    reviewUpdate: false,
    authorsList: false,
    generate: false,
    generateNew: false,
    addressList: false,
    quoteStatusDashboard: false,
    mostRatedCategories: false,
    redoQuote: false
  },

  requestingStatus: [],

  pagination: {
    current: 1,
    limit: 1
  },

  selected: {
    mode: cardViewTypes.list,
    status: [],
    request: null,
    characteristic: [],
    order: 1,
    authors: [],
    startDate: null,
    endDate: null,
    addressId: null
  },

  modeList: [
    { id: cardViewTypes.list, type: "list", name: "Em lista", icon: iconGrid3 },
    { id: cardViewTypes.grid, type: "grid", name: "Em grade", icon: iconGrid2 }
  ],

  searchTerm: [],

  reviewList: [],
  requestList: [],
  authorsList: [],
  addressList: [],
  quoteStatusDashboard: {
    inProgress: 0,
    inNegociation: 0,
    approved: 0
  },
  mostRatedCategories: [],

  shareQuotation: {
    expireAt: null,
    link: null,
    accessCount: 0,
    isExpired: false
  }
};

const mutations = {
  request_start(state, type) {
    state.requesting[type] = true;
  },

  request_success(state, type) {
    state.requesting[type] = false;
  },

  request_failure(state, type) {
    state.requesting[type] = false;
  },

  add_request_status(state, id) {
    const statusList = state.requestingStatus.filter(item => item !== id);

    state.requestingStatus = [...statusList, id];
  },

  remove_request_status(state, id) {
    state.requestingStatus = state.requestingStatus.filter(item => item != id);
  },

  set_request_list(state, requestList) {
    state.requestList = requestList;
  },

  set_pagination(state, pagination) {
    Object.assign(state.pagination, pagination);
  },

  set_selected_order(state, order) {
    Object.assign(state.selected.order, order);
  },

  set_selected(state, selected) {
    Object.assign(state.selected, selected);
  },

  set_search_term(state, searchTerm) {
    state.searchTerm = searchTerm;
  },

  set_review_list(state, reviewList) {
    state.reviewList = reviewList;
  },

  reset_review_list(state) {
    state.reviewList.length = 0;
  },

  set_authors_list(state, authorsList) {
    state.authorsList = authorsList;
  },

  set_address_list(state, addressList) {
    state.addressList = addressList;
  },

  set_share_quotation(state, shareQuotation) {
    state.shareQuotation = shareQuotation;
  },

  set_quote_status_dashboard(state, data) {
    state.quoteStatusDashboard = data;
  },

  set_most_rated_categories(state, data) {
    state.mostRatedCategories = data;
  }
};

const actions = {
  // #region List
  async getList({ commit, dispatch, state }) {
    commit("request_start", "requestList");
    const { pagination, searchTerm, selected } = state;
    try {
      const { data } = await getRequestList(pagination.current, {
        filter: searchTerm,
        status: selected.status,
        order: selected.order,
        characteristic: selected.characteristic,
        authors: selected.authors,
        startDate: selected.startDate,
        endDate: selected.endDate,
        addressId: selected.addressId
      });
      const requestList = data.items.map(parseRequestPreview);
      commit("set_request_list", requestList);

      const limit = data.pageCount;
      commit("set_pagination", { limit });

      commit("request_success", "requestList");
      dispatch("getListStatus");

      return requestList;
    } catch (err) {
      commit("request_failure", "requestList");

      throw err;
    }
  },

  async getLastBudgets({ commit, dispatch, state }) {
    commit("request_start", "requestList");
    const { pagination, searchTerm, selected } = state;
    try {
      const { data } = await getLastBudgetList(pagination.current, {
        filter: searchTerm,
        status: selected.status,
        order: selected.order,
        characteristic: selected.characteristic,
        authors: selected.authors,
        startDate: selected.startDate,
        endDate: selected.endDate,
        addressId: selected.addressId
      });
      const requestList = data.items.map(parseLastBudget);
      commit("set_request_list", requestList);

      const limit = data.pageCount;
      commit("set_pagination", { limit });

      commit("request_success", "requestList");
      dispatch("getListStatus");

      return requestList;
    } catch (err) {
      commit("request_failure", "requestList");

      throw err;
    }
  },

  async getLastMessages({ commit, dispatch, state }) {
    commit("request_start", "requestList");
    const { pagination, searchTerm, selected } = state;
    try {
      const { data } = await getLastMessageList(pagination.current, {
        filter: searchTerm,
        status: selected.status,
        order: selected.order,
        characteristic: selected.characteristic,
        authors: selected.authors,
        startDate: selected.startDate,
        endDate: selected.endDate,
        addressId: selected.addressId
      });
      const requestList = data.items.map(parseLastMessages);
      commit("set_request_list", requestList);

      const limit = data.pageCount;
      commit("set_pagination", { limit });

      commit("request_success", "requestList");
      dispatch("getListStatus");

      return requestList;
    } catch (err) {
      commit("request_failure", "requestList");

      throw err;
    }
  },

  async getArchivedQuotations({ commit, dispatch, state }) {
    commit("request_start", "requestList");
    const { pagination, searchTerm, selected } = state;
    try {
      const { data } = await getArchivedQuotations(pagination.current, {
        filter: searchTerm,
        status: selected.status,
        order: selected.order,
        characteristic: selected.characteristic,
        authors: selected.authors,
        startDate: selected.startDate,
        endDate: selected.endDate,
        addressId: selected.addressId
      });
      const requestList = data.items.map(parseRequestPreview);
      commit("set_request_list", requestList);

      const limit = data.pageCount;
      commit("set_pagination", { limit });

      commit("request_success", "requestList");
      dispatch("getListStatus");

      return requestList;
    } catch (err) {
      commit("request_failure", "requestList");

      throw err;
    }
  },

  async getSupplierEvaluations({ commit, dispatch, state }) {
    commit("request_start", "requestList");
    const { pagination, searchTerm, selected } = state;
    try {
      const { data } = await getSupplierEvaluationList(pagination.current, {
        filter: searchTerm,
        status: selected.status,
        order: selected.order,
        characteristic: selected.characteristic,
        authors: selected.authors,
        startDate: selected.startDate,
        endDate: selected.endDate,
        addressId: selected.addressId
      });
      const requestList = data.items.map(parseRequestPreview);
      commit("set_request_list", requestList);

      const limit = data.pageCount;
      commit("set_pagination", { limit });

      commit("request_success", "requestList");
      dispatch("getListStatus");

      return requestList;
    } catch (err) {
      commit("request_failure", "requestList");

      throw err;
    }
  },
  async getLastestActivities({ commit, dispatch, state }) {
    commit("request_start", "requestList");
    const { pagination, searchTerm, selected } = state;
    try {
      const { data } = await getLastestActivitiesList(pagination.current, {
        filter: searchTerm,
        status: selected.status,
        startDate: selected.startDate,
        endDate: selected.endDate,
        addressId: selected.addressId
      });
      const requestList = data.items.map(parseLastestActivities);
      commit("set_request_list", requestList);

      const limit = data.pageCount;
      commit("set_pagination", { limit });

      commit("request_success", "requestList");
      dispatch("getListStatus");

      return requestList;
    } catch (err) {
      commit("request_failure", "requestList");

      throw err;
    }
  },

  async getQuoteStatusDashboard({ commit, dispatch, state }, requestPath) {
    commit("request_start", "quoteStatusDashboard");
    const { pagination, searchTerm, selected } = state;
    try {
      const { data } = await getQuoteStatus(
        pagination.current,
        {
          filter: searchTerm,
          status: selected.status,
          order: selected.order,
          characteristic: selected.characteristic,
          authors: selected.authors,
          startDate: selected.startDate,
          endDate: selected.endDate,
          addressId: selected.addressId
        },
        requestPath
      );
      const quoteStatus = data;
      commit("set_quote_status_dashboard", quoteStatus);

      const limit = data.pageCount;
      commit("set_pagination", { limit });

      commit("request_success", "quoteStatusDashboard");

      return quoteStatus;
    } catch (err) {
      commit("request_failure", "quoteStatusDashboard");

      throw err;
    }
  },

  async getRequestStatus({ commit }, requestPreview) {
    commit("add_request_status", requestPreview.id);

    try {
      const { data } = await getRequestStatus(requestPreview.id);
      const updatedRequest = parseRequestPreviewStatus(data)(requestPreview);

      commit("remove_request_status", updatedRequest);

      return updatedRequest;
    } catch (err) {
      commit("remove_request_status", requestPreview.id);

      throw err;
    }
  },

  async getListStatus({ commit, dispatch, state }) {
    commit("request_start", "status");

    try {
      const resultList = await Promise.all(
        state.requestList.map(async request => {
          const requestPreview = await dispatch("getRequestStatus", request);

          return requestPreview;
        })
      );
      const requestList = intersectionBy(resultList, state.requestList, "id");
      commit("set_request_list", requestList);
      commit("request_success", "status");

      return requestList;
    } catch (err) {
      commit("request_failure", "status");

      throw err;
    }
  },

  async searchRequest({ commit, dispatch }, searchTerm) {
    try {
      commit("set_search_term", searchTerm);
    } catch (err) {
      throw err;
    }
  },

  async searchRequestLegacy({ commit, dispatch }, searchTerm) {
    commit("set_search_term", searchTerm);

    try {
      const result = await dispatch("getList");

      return result;
    } catch (err) {
      throw err;
    }
  },

  async getDetails({ commit }, quotationId) {
    commit("request_start", "details");

    try {
      const { data } = await getRequest(quotationId);
      const request = parseRequestDetails(data);
      commit("set_selected", { request });
      commit("request_success", "details");
      return request;
    } catch (err) {
      commit("request_failure", "details");

      throw err;
    }
  },

  async redoQuote({ commit }) {
    commit("request_start", "redoQuote");

    try {
      const data = redoQuoteParser(this.state.quoteRequest.selected.request);
      const result = await sendQuotation(data);

      commit("request_success", "redoQuote");

      return result;
    } catch (err) {
      commit("request_failure", "redoQuote");

      throw err;
    }
  },

  async updateStatus({ commit }, { quotationId, status }) {
    commit("request_start", "status");

    try {
      const result = await updateRequestStatus(quotationId, status);
      commit("request_success", "status");

      return result;
    } catch (err) {
      commit("request_failure", "status");

      throw err;
    }
  },

  async getMostRatedCategories({ commit }) {
    commit("request_start", "mostRatedCategories");

    try {
      const { data } = await getQuoteMostRatedCategories();
      commit("request_success", "mostRatedCategories");

      commit("set_most_rated_categories", data);

      return data;
    } catch (err) {
      commit("request_failure", "mostRatedCategories");

      throw err;
    }
  },

  async changePage({ commit, dispatch }, { current, requestType = "getList" }) {
    try {
      commit("set_pagination", { current });
      const result = await dispatch(requestType);
    } catch (err) {
      throw err;
    }
  },

  async changeFilter({ commit, dispatch }, filter: any, api: string) {
    commit("set_pagination", { current: 1, limit: 1 });

    let { selected } = JSON.parse(JSON.stringify(state));

    if (filter.order) selected.order = filter.order;
    if (filter.characteristic) selected.characteristic = filter.characteristic;
    if (filter.authors) selected.authors = filter.authors;
    if (filter.status) selected.status = filter.status;
    if (filter.addressId) selected.addressId = filter.addressId;
    if (filter.date) {
      const startDate = formatDate(
        new Date(filter.date.startDate),
        "DD/MM/YYYY"
      );
      const endDate = formatDate(new Date(filter.date.endDate), "DD/MM/YYYY");
      selected.startDate = startDate;
      selected.endDate = endDate;
    }

    commit("set_selected", selected);

    // const ststus = await dispatch('getQuoteStatusDashboard')
    // const result = await dispatch('getList')
  },

  async changeFilterLegacy({ commit, dispatch }, filter: any, api: string) {
    commit("set_pagination", { current: 1, limit: 1 });

    let { selected } = JSON.parse(JSON.stringify(state));

    if (filter.order) selected.order = filter.order;
    if (filter.characteristic) selected.characteristic = filter.characteristic;
    if (filter.authors) selected.authors = filter.authors;
    if (filter.status) selected.status = filter.status;
    if (filter.addressId) selected.addressId = filter.addressId;
    if (filter.date) {
      const startDate = formatDate(
        new Date(filter.date.startDate),
        "DD/MM/YYYY"
      );
      const endDate = formatDate(new Date(filter.date.endDate), "DD/MM/YYYY");
      selected.startDate = startDate;
      selected.endDate = endDate;
    }

    commit("set_selected", selected);

    const ststus = await dispatch("getQuoteStatusDashboard");
    const result = await dispatch("getList");
    return result;
  },

  async resetFilters({ commit, dispatch }) {
    commit("set_pagination", { current: 1, limit: 1 });

    let { selected } = JSON.parse(JSON.stringify(state));
    (selected.mode = cardViewTypes.list),
      (selected.status = []),
      (selected.request = null),
      (selected.characteristic = []),
      (selected.order = 1),
      (selected.authors = []),
      (selected.startDate = null),
      (selected.endDate = null),
      (selected.addressId = null);
    commit("set_selected", selected);
    const ststus = await dispatch("getQuoteStatusDashboard");

    // return result
  },

  updateSelected({ commit }, payload) {
    commit("set_selected", payload);
  },

  async getListAuthors({ commit }) {
    commit("request_start", "authorsList");

    try {
      const { data } = await getAuthorsList();

      commit("set_authors_list", data);

      commit("request_success", "authorsList");
      return data;
    } catch (err) {
      commit("request_failure", "authorsList");

      throw err;
    }
  },

  async getListAddress({ commit }) {
    commit("request_start", "addressList");

    try {
      const { data } = await getAddressList();

      const parsedData = data.map(item => ({
        value: item.addressId,
        name: item.description
      }));

      commit("set_address_list", parsedData);

      commit("request_success", "addressList");
      return data;
    } catch (err) {
      commit("request_failure", "addressList");

      throw err;
    }
  },
  // #endregion

  // #region Review
  resetReviewList({ commit }) {
    commit("reset_review_list");
  },

  async getReviews({ commit }, quotationId) {
    commit("request_start", "reviewList");

    try {
      const { data } = await getRequestReview(quotationId);
      const reviewList = data.map(parseRequestReview);

      commit("set_review_list", reviewList);
      commit("request_success", "reviewList");

      return data;
    } catch (err) {
      commit("request_failure", "reviewList");

      throw err;
    }
  },

  async addReview({ commit }, review) {
    commit("request_start", "reviewUpdate");

    try {
      const formatedReview = formatRequestReviewAdd(review);
      const result = await addCompanyReview(formatedReview);

      commit("request_success", "reviewUpdate");

      return result;
    } catch (err) {
      commit("request_failure", "reviewUpdate");

      throw err;
    }
  },

  async updateReview({ commit }, review) {
    commit("request_start", "reviewUpdate");

    try {
      const formatedReview = formatRequestReviewUpdate(review);
      const result = await updateCompanyReview(formatedReview);

      commit("request_success", "reviewUpdate");

      return result;
    } catch (err) {
      commit("request_failure", "reviewUpdate");

      throw err;
    }
  },
  // #endregion

  // #region share
  async generateReadOnlyQuotation({ commit }, quotationId) {
    commit("request_start", "generate");

    try {
      const { data } = await generateReadOnly(quotationId);
      const formattedData = formatGenerateReadOnly(data);

      commit("set_share_quotation", formattedData);

      commit("request_success", "generate");

      return formattedData;
    } catch (err) {
      commit("request_failure", "generate");

      throw err;
    }
  },

  async generateNewReadOnlyQuotation({ commit }, quotationId) {
    commit("request_start", "generateNew");

    try {
      const { data } = await generateNewReadOnly(quotationId);
      const formattedData = formatGenerateReadOnly(data);

      commit("set_share_quotation", formattedData);

      commit("request_success", "generateNew");

      return formattedData;
    } catch (err) {
      commit("request_failure", "generateNew");

      throw err;
    }
  }
  // #endregion
};

export default {
  namespaced: true,

  state,
  mutations,
  actions
};
